import React from "react";
import { FaTimes } from "react-icons/fa";
import { CaptionToken, isLowConfidence } from "./zapcapCaptionEditor";
import { cn } from "../../../utils/classMerger";

interface CaptionTokenEditorProps {
    tokens: CaptionToken[];
    editingId: string | null;
    editingValue: string;
    setEditingValue: (value: string) => void;
    onCommitEdit: () => void;
    onCancelEdit: () => void;
    onDeleteEditing: () => void;
    onTokenClick: (token: CaptionToken) => void;
}

// MB-1461 — the word-token grid extracted from MB-1460's
// EditZapcapCaptionsDialog so the in-wizard pre-render review (phase 2) can
// reuse the exact same editor. Pure presentation: all state lives in the
// caller and every mutation goes through the zapcapCaptionEditor reducer.
const CaptionTokenEditor: React.FC<CaptionTokenEditorProps> = ({
    tokens,
    editingId,
    editingValue,
    setEditingValue,
    onCommitEdit,
    onCancelEdit,
    onDeleteEditing,
    onTokenClick,
}) => (
    <>
        <div className="flex w-full min-w-0 flex-wrap items-center gap-x-1 gap-y-2 overflow-x-hidden rounded-md border border-gray-200 p-3 leading-8 min-[640px]:p-4">
            {tokens.map((token) =>
                editingId === token.id ? (
                    <span
                        key={token.id}
                        className="inline-flex min-w-0 max-w-full items-center gap-1"
                    >
                        <input
                            autoFocus
                            value={editingValue}
                            onChange={(e) => setEditingValue(e.target.value)}
                            onBlur={onCommitEdit}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") onCommitEdit();
                                if (e.key === "Escape") onCancelEdit();
                            }}
                            size={Math.min(Math.max(editingValue.length, 3), 24)}
                            className="min-w-0 max-w-full rounded border border-primary px-1 py-0.5 text-sm focus:outline-none"
                        />
                        <button
                            aria-label="Delete word"
                            title="Delete this word"
                            // mousedown so it fires before the input's blur
                            // commit.
                            onMouseDown={(e) => {
                                e.preventDefault();
                                onDeleteEditing();
                            }}
                            className="shrink-0 text-red-500 hover:text-[#d50000]"
                        >
                            <FaTimes className="h-3 w-3" />
                        </button>
                    </span>
                ) : (
                    <button
                        key={token.id}
                        onClick={() => onTokenClick(token)}
                        title={
                            token.deleted
                                ? "Click to restore"
                                : isLowConfidence(token)
                                  ? "Low transcription confidence - double-check this word"
                                  : "Click to edit"
                        }
                        className={cn(
                            "max-w-full whitespace-normal break-all rounded px-1 py-0.5 text-left text-sm transition-colors",
                            token.deleted
                                ? "bg-gray-100 text-[#d50000] line-through"
                                : token.text.trim() !==
                                    token.originalText.trim()
                                  ? "bg-[#beeb9f] text-[#193456]"
                                  : isLowConfidence(token)
                                    ? "bg-[#fff176] text-[#193456] underline decoration-[#ffbe00] decoration-wavy"
                                    : "hover:bg-gray-100"
                        )}
                    >
                        {token.text}
                    </button>
                )
            )}
        </div>
        <p className="text-xs text-gray-500">
            Highlighted words had low transcription confidence. Click a word to
            fix or delete it - deleted words can be clicked again to restore
            them. New words can&apos;t be inserted.
        </p>
    </>
);

export default CaptionTokenEditor;
